/** @format */

import React, { useEffect, useRef } from 'react';
import { trackClickEvent } from '@services/applicationInsights';

interface ClickTrackerProps {
  children: React.ReactNode;
}

/**
 * HOC Component that automatically tracks all click events within its children
 * Wraps the entire app to capture all user interactions
 *
 * Usage: <ClickTracker><App /></ClickTracker>
 */
export const ClickTracker: React.FC<ClickTrackerProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Get element details
      const elementName =
        target.getAttribute('data-tracking-name') ||
        target.getAttribute('aria-label') ||
        target.getAttribute('title') ||
        target.textContent?.substring(0, 50) ||
        target.tagName;

      const elementType = target.tagName.toLowerCase();
      const elementId = target.id;
      const elementClass = target.className;
      const href = target.getAttribute('href');

      // Get element position in viewport
      const rect = target.getBoundingClientRect();

      // Build tracking properties
      const properties: { [key: string]: any } = {
        elementId: elementId || undefined,
        elementClass: typeof elementClass === 'string' ? elementClass : undefined,
        href: href || undefined,
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        pageX: event.pageX,
        pageY: event.pageY,
        timestamp: new Date().toISOString(),
        currentPath: window.location.pathname,
        currentUrl: window.location.href
      };

      // Track the click
      trackClickEvent(elementName, elementType, properties);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('click', handleClick, true);
    }

    return () => {
      if (container) {
        container.removeEventListener('click', handleClick, true);
      }
    };
  }, []);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }}>{children}</div>;
};
